// MessageReactions.js - Interactive reaction system for messages
import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { 
  Box, 
  Chip, 
  Tooltip, 
  Typography, 
  Collapse,
  IconButton,
  Paper,
  ClickAwayListener,
  Fade
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  ThumbUp as ThumbUpIcon,
  Favorite as FavoriteIcon,
  EmojiEmotions as LaughIcon,
  Whatshot as WowIcon,
  SentimentDissatisfied as SadIcon,
  Mood as AngryIcon,
  Add as AddIcon
} from '@mui/icons-material';

const REACTION_EMOJIS = {
  like: '👍',
  love: '❤️',
  laugh: '😂',
  wow: '😮',
  sad: '😢',
  angry: '😠'
};

const REACTION_ICONS = {
  like: ThumbUpIcon,
  love: FavoriteIcon,
  laugh: LaughIcon,
  wow: WowIcon,
  sad: SadIcon,
  angry: AngryIcon
};

const MessageReactions = forwardRef(function MessageReactions(
  {
    message,
    isUserMessage,
    reactions = {},
    totalReactions = 0,
    userReactions = [],
    onReactionAdd,
    onReactionRemove,
    disabled = false
  },
  ref
) {
  const theme = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);
  const activeReactions = Array.isArray(userReactions) ? userReactions : [];

  useImperativeHandle(ref, () => ({
    open: () => setPickerOpen(true),
    close: () => setPickerOpen(false),
  }), []);

  const handleReactionClick = async (reactionType) => {
    if (disabled) return;

    try {
      if (activeReactions.includes(reactionType)) {
        if (onReactionRemove) {
          await onReactionRemove(message.id, reactionType);
        }
      } else {
        if (onReactionAdd) {
          await onReactionAdd(message.id, reactionType);
        }
      }
    } catch (error) {
      console.error('Reaction error:', error);
    } finally {
      setPickerOpen(false);
    }
  };

  const sortedReactions = Object.entries(reactions)
    .filter(([_, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <Box sx={{ position: 'relative' }}>
      {/* Reaction Picker */}
      {pickerOpen && (
        <ClickAwayListener onClickAway={() => setPickerOpen(false)}>
          <Fade in={pickerOpen}>
            <Paper
              elevation={8}
              sx={{
                position: 'absolute',
                bottom: '100%',
                [isUserMessage ? 'right' : 'left']: 0,
                mb: 1,
                p: 1,
                display: 'flex',
                gap: 0.5,
                borderRadius: 3,
                bgcolor: 'background.paper',
                border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                zIndex: 1000
              }}
            >
              {Object.entries(REACTION_EMOJIS).map(([type, emoji]) => {
                const isSelected = activeReactions.includes(type);
                
                return (
                  <Tooltip key={type} title={type.charAt(0).toUpperCase() + type.slice(1)} arrow>
                    <IconButton
                      size="small"
                      onClick={() => handleReactionClick(type)}
                      disabled={disabled}
                      sx={{
                        fontSize: '1.5rem',
                        width: 40,
                        height: 40,
                        bgcolor: isSelected 
                          ? alpha(theme.palette.primary.main, 0.1) 
                          : 'transparent',
                        border: isSelected 
                          ? `2px solid ${theme.palette.primary.main}` 
                          : '2px solid transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.2)',
                          bgcolor: alpha(theme.palette.primary.main, 0.1)
                        }
                      }}
                    >
                      {emoji}
                    </IconButton>
                  </Tooltip>
                );
              })}
            </Paper>
          </Fade>
        </ClickAwayListener>
      )}

      {/* Display Reactions */}
      <Collapse in={totalReactions > 0 || pickerOpen}>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            justifyContent: isUserMessage ? 'flex-end' : 'flex-start',
            alignItems: 'center'
          }}
        >
          {sortedReactions.map(([type, count]) => {
            const isActive = activeReactions.includes(type);
            return (
              <Tooltip 
                key={type} 
                title={`${count} ${type}${count !== 1 ? 's' : ''}`} 
                arrow
              >
                <Chip
                  size="small"
                  onClick={() => !disabled && handleReactionClick(type)}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <span style={{ fontSize: '0.875rem' }}>
                        {REACTION_EMOJIS[type]}
                      </span>
                      <Typography variant="caption" sx={{ fontWeight: 500 }}>
                        {count}
                      </Typography>
                    </Box>
                  }
                  variant={isActive ? 'filled' : 'outlined'}
                  color={isActive ? 'primary' : 'default'}
                  sx={{
                    height: 24,
                    bgcolor: isActive
                      ? alpha(theme.palette.primary.main, 0.15)
                      : alpha(theme.palette.action.hover, 0.05),
                    borderColor: isActive
                      ? theme.palette.primary.main
                      : alpha(theme.palette.divider, 0.3),
                    cursor: disabled ? 'default' : 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': disabled ? {} : {
                      transform: 'scale(1.05)',
                      bgcolor: isActive
                        ? alpha(theme.palette.primary.main, 0.25)
                        : alpha(theme.palette.action.hover, 0.1),
                    }
                  }}
                />
              </Tooltip>
            );
          })}

          {/* Add Reaction Button */}
          {!disabled && (
            <Tooltip title="Add reaction" arrow>
              <IconButton
                size="small"
                onClick={() => setPickerOpen(!pickerOpen)}
                sx={{
                  width: 24,
                  height: 24,
                  opacity: 0.5,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    opacity: 1,
                    bgcolor: alpha(theme.palette.action.hover, 0.1)
                  }
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Collapse>
    </Box>
  );
});

export default MessageReactions;